import React from 'react';
import { PlanCurrencyCode } from '../../../types/api';
import { CUSTOM_PLAN_NAME, SubscriptionPlanView } from '../types/subscription';
import { formatRegionalAmount, formatSubscriberCount } from '../subscriptionData';

interface SubscriptionPlanCardProps {
  plan: SubscriptionPlanView;
  displayCurrency: PlanCurrencyCode;
  onEdit: (plan: SubscriptionPlanView) => void;
  onDeactivate: (plan: SubscriptionPlanView) => void;
  canManage?: boolean;
}

const renderMonthlyPrice = (plan: SubscriptionPlanView, currency: PlanCurrencyCode) => {
  if (plan.isFree && !plan.isDynamic) {
    return <span className="subscription-plan-card__price">Free</span>;
  }

  const price = plan.monthlyPrice?.[currency];
  if (!price) {
    return <span className="subscription-plan-card__price">—</span>;
  }

  return (
    <>
      <span className="subscription-plan-card__price">
        {formatRegionalAmount(currency, price.amount)}
      </span>
      <span className="subscription-plan-card__period">/mo</span>
    </>
  );
};

const renderBillingLine = (plan: SubscriptionPlanView, currency: PlanCurrencyCode) => {
  if (plan.isFree && !plan.isDynamic) {
    return 'Free plan — no billing required';
  }

  const yearly = plan.yearlyPrice?.[currency];

  if (!plan.yearlyPrice || !yearly) {
    return 'Monthly billing available';
  }

  return `or ${formatRegionalAmount(currency, yearly.amount)}/yr • Monthly/Yearly`;
};

export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  plan,
  displayCurrency,
  onEdit,
  onDeactivate,
  canManage = true,
}) => (
  <article
    className={`subscription-plan-card ${plan.isPopular ? 'subscription-plan-card--popular' : ''} ${plan.isFree && !plan.isDynamic ? 'subscription-plan-card--free' : ''}`}
  >
    {plan.isDynamic && (
      <span
        className="subscription-plan-card__popular"
        style={
          plan.isPopular
            ? { right: 'auto', left: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 5 }
            : undefined
        }
      >
        {CUSTOM_PLAN_NAME}
      </span>
    )}
    {plan.isPopular && <span className="subscription-plan-card__popular">Popular</span>}

    <div className="subscription-plan-card__top">
      <span
        className={`subscription-plan-card__status ${plan.isHidden ? 'subscription-plan-card__status--hidden' : ''}`}
      >
        {plan.isHidden ? 'Hidden' : 'Active'}
      </span>
      <h2>{plan.name}</h2>
      <div className="subscription-plan-card__price-row">
        {renderMonthlyPrice(plan, displayCurrency)}
      </div>
      <p className="subscription-plan-card__billing">
        {renderBillingLine(plan, displayCurrency)}
      </p>
    </div>

    <div className="subscription-plan-card__features">
      <h3 className="subscription-plan-card__section-title">Included Features</h3>
      {plan.features.length > 0 ? (
        <ul className="subscription-plan-card__feature-list">
          {plan.features.map((feature) => (
            <li key={feature} className="subscription-plan-card__feature">
              <span className="subscription-plan-card__check" aria-hidden="true">
                <svg viewBox="0 0 12 12" focusable="false">
                  <path d="M9.8 3.2 5.1 8 2.4 5.3" />
                </svg>
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="subscription-plan-card__features-empty">No features configured</p>
      )}
    </div>

    <div className="subscription-plan-card__stats">
      <span className="subscription-plan-card__stats-label">Subscribers</span>
      <strong className="subscription-plan-card__stats-value">
        {formatSubscriberCount(plan.subscribers)}
      </strong>
    </div>

    {canManage && (
    <div className="subscription-plan-card__actions">
      <button
        type="button"
        className="subscription-plan-card__btn subscription-plan-card__btn--edit"
        onClick={() => onEdit(plan)}
      >
        Edit Plan
      </button>
      <button
        type="button"
        className="subscription-plan-card__btn subscription-plan-card__btn--deactivate"
        onClick={() => {
          if (plan.isDynamic) return;
          onDeactivate(plan);
        }}
        disabled={plan.isDynamic}
        title={plan.isDynamic ? 'Custom Plan cannot be activated or deactivated' : undefined}
        style={plan.isDynamic ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
      >
        {plan.isHidden ? 'Activate' : 'Deactivate'}
      </button>
    </div>
    )}
  </article>
);
